import { useState } from 'react'
import { Clock, Edit2, Trash2, ChevronDown } from 'lucide-react'
import { getVisibleBranches, getFilteredEntries } from '../utils/dataHelpers'
import { formatNaira, formatLiters, FUEL_COLORS } from '../utils/formatters'
import { todayStr } from '../utils/dateFilters'
import { calcRevenue, calcMeasuredExcessProfit, calcTankSales } from '@petrotrack/shared'

const DATE_OPTIONS = [
  { value: 'all', label: 'All Time' }, { value: 'today', label: 'Today' },
  { value: 'last7', label: 'Last 7 days' }, { value: 'last30', label: 'Last 30 days' },
]
const formatDateHeader = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })

export default function HistoryPage({ currentUser, organizations, branches, entries, onEditEntry, onDeleteEntry }) {
  const [dateFilter, setDateFilter] = useState('last7')
  const today = todayStr()
  const canEditEntry = (e) => currentUser?.role === 'superadmin' || (currentUser?.role === 'manager' && e.date === today)

  const filters = { dateFilter, fuelTypeFilter: 'all', selectedOrg: 'all', selectedBranch: 'all', customRange: { start: '', end: '' } }
  const visibleBranches = getVisibleBranches(currentUser, branches, filters)
  const branchById = Object.fromEntries(visibleBranches.map((b) => [b.id, b]))
  const ids = new Set(visibleBranches.map((b) => b.id))
  const rows = getFilteredEntries(entries, ids, filters)

  const byDate = {}
  for (const e of rows) (byDate[e.date] ??= []).push(e)
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a))

  return (
    <div className="animate-fadeIn space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-800">History</h1>
        <div className="relative">
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="appearance-none bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            {DATE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {sortedDates.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-10 text-center"><Clock className="w-12 h-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-500 font-medium">No entries found</p><p className="text-xs text-gray-400 mt-1">Try a wider date range</p></div>
      ) : (
        <div className="space-y-5">
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{formatDateHeader(date)}</span>
                <span className="text-xs text-gray-300">&bull; {byDate[date].length} entr{byDate[date].length === 1 ? 'y' : 'ies'}</span>
              </div>
              <div className="space-y-2">
                {byDate[date].map((entry) => {
                  const branch = branchById[entry.branchId]
                  const org = organizations.find((o) => o.id === branch?.organizationId)
                  const colors = FUEL_COLORS[entry.product] || FUEL_COLORS.PMS
                  return (
                    <div key={entry.id} className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
                      <div className="flex items-start justify-between mb-2.5">
                        <div className="flex items-center space-x-2 min-w-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${colors.bg} ${colors.text}`}>{entry.product}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{branch?.name || 'Station'}</p>
                            {org && <p className="text-xs text-gray-400 truncate">{org.name}</p>}
                          </div>
                        </div>
                        {canEditEntry(entry) && (
                          <div className="flex items-center space-x-0.5 flex-shrink-0 ml-2">
                            <button onClick={() => onEditEntry(branch, entry)} className="w-8 h-8 flex items-center justify-center rounded-lg text-blue-500 hover:bg-blue-50"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => onDeleteEntry(entry)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-400 mb-0.5">Tank Sales</p><p className="text-xs font-semibold text-gray-700">{formatLiters(calcTankSales(entry))}</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-400 mb-0.5">Revenue</p><p className="text-xs font-semibold text-gray-700">{formatNaira(calcRevenue(entry))}</p></div>
                        <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-400 mb-0.5">Excess Profit</p><p className={`text-xs font-semibold ${calcMeasuredExcessProfit(entry) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatNaira(calcMeasuredExcessProfit(entry))}</p></div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
